import { useCallback, useEffect, useState } from 'react'

import { ApiError } from '@forbiz/web-kit'

interface AsyncState<T> {
  data: T | null
  loading: boolean
  error: string | null
  reload: () => void
  /** 서버 재요청 없이 로컬 데이터를 갈아끼운다 (예: 전이 응답으로 즉시 반영). */
  mutate: (next: T) => void
}

/**
 * GET 데이터 로딩 훅. 목록·상세에 쓴다.
 * deps가 바뀌면 다시 불러온다. reload()로 수동 갱신, mutate()로 재요청 없이 즉시 반영.
 */
export function useAsync<T>(fetcher: () => Promise<T>, deps: unknown[]): AsyncState<T> {
  const [data, setData] = useState<T | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [tick, setTick] = useState(0)

  const reload = useCallback(() => setTick((t) => t + 1), [])
  const mutate = useCallback((next: T) => setData(next), [])

  useEffect(() => {
    let alive = true
    setLoading(true)
    setError(null)
    fetcher()
      .then((d) => {
        if (alive) setData(d)
      })
      .catch((err) => {
        if (!alive) return
        setError(err instanceof ApiError ? err.message : '불러오지 못했습니다')
      })
      .finally(() => {
        if (alive) setLoading(false)
      })
    return () => {
      alive = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, tick])

  return { data, loading, error, reload, mutate }
}
